/* AddInstructionsAction.js */

"use strict";
const AddInstructionsComponent = require("./AddInstructionsComponent");
const AddInstructionsSummaryComponent = require("./summary/AddInstructionsSummaryComponent");

/**
 * Action for adding instructions to IoT Platforms.
 * Provides three modes for instruction management:
 * 1. Create from text - Manual text entry
 * 2. Create from file - Upload PDF/DOCX files
 * 3. Select existing - Link pre-existing instructions
 *
 * Action Availability:
 * - Enabled when one or more IoT Platforms are selected
 * - Disabled when no IoT Platform is selected
 *
 * Workflow:
 * 1. User selects platforms in grid
 * 2. Action loads available instructions for organization
 * 3. Displays AddInstructionsComponent veil
 * 4. On success, refreshes grid to show updated instruction counts
 * 5. On partial failure, displays AddInstructionsSummaryComponent with error details
 */
class AddInstructionsAction {
    /**
     * Creates an instance of AddInstructionsAction
     * @param {Object} logger - Logger instance for debugging
     * @param {Object} gameEditor - Game editor instance with active view and org ID
     * @param {Object} modalUserFeedback - Modal service for displaying veils
     * @param {Function} OdmOsInstruction - OdmOsInstruction entity constructor
     * @param {Function} OdmOsPlatform - OdmOsPlatform entity constructor
     * @param {Object} userSession - Current user session object
     */
    constructor(
        logger,
        gameEditor,
        modalUserFeedback,
        OdmOsInstruction,
        OdmOsPlatform,
        userSession
    ) {
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._modalUserFeedback = modalUserFeedback;
        this._OdmOsInstruction = OdmOsInstruction;
        this._OdmOsPlatform = OdmOsPlatform;
        this._userSession = userSession;
        this._activeView = this._gameEditor._activeView;

        // Detect if we're in edit mode (editing single instruction) or bulk add mode
        this._isEditMode = this._activeView.isEditingInstruction || false;
        this._instructionToEdit = this._isEditMode
            ? this._activeView._selectedInstructionForEdit
            : null;

        // Extract selected platform IDs from the active view (only for bulk add mode)
        this._selectedPlatformIds = [];
        if (!this._isEditMode && this._activeView && this._activeView.pagingFeed) {
            this._selectedPlatformIds =
                this._activeView.getSelectedOdmOsPlatformIds().selectedRows || [];
        } else {
            // In edit mode, we're working with a single platform (the one that owns the instruction)
            this._selectedPlatformIds = [this._activeView._odmOsPlatform.getId()];
        }

        // Detect editor mode: in editor, activeView has _odmOsPlatform property
        // In explorer, activeView has pagingFeed but no _odmOsPlatform
        // This is used for dropdown filtering to exclude already-linked instructions
        const odmOsPlatform = this._activeView?._odmOsPlatform;
        const isEditorMode = this._activeView && odmOsPlatform ? true : false;
        this._isEditorMode = isEditorMode;
        this._selectedTarget = isEditorMode ? odmOsPlatform : null;

        // Bind run method for use as action callback
        this.run = this.run.bind(this);
    }

    /**
     * Executes the Add Instructions action.
     *
     * Workflow:
     * 1. Validates platform selection
     * 2. Loads available instructions for organization
     * 3. Displays AddInstructionsComponent veil
     * 4. Handles result (success, partial failure, or user cancellation)
     * 5. Shows summary veil if there were errors
     * 6. Refreshes grid to update instruction counts
     *
     * @returns {Promise} Promise that resolves when action completes
     */
    run() {
        // Validate that platforms are selected (or we're in edit mode with an instruction)
        if (!this._isEditMode && this._selectedPlatformIds.length === 0) {
            this._logger.warn("AddInstructionsAction.run() - No platforms selected");
            return Promise.resolve();
        }

        if (this._isEditMode && !this._instructionToEdit) {
            this._logger.warn(
                "AddInstructionsAction.run() - Edit mode but no instructions to edit"
            );
            return Promise.resolve();
        }

        /**
         * Load available instructions with optimization:
         * - Edit mode: Return only the instruction being edited
         * - Editor mode: Exclude already-linked instructions via database query
         * - Explorer mode: Load all instructions for the organization
         */
        const loadPromise = this._isEditMode
            ? Promise.resolve([this._instructionToEdit])
            : this._isEditorMode
            ? this._selectedTarget
                  .loadRelatedInstructions({}, {id: true})
                  .then((existingLinkedInstructions) => {
                      // Extract IDs of already-linked instructions for query exclusion
                      const existingLinkedInstructionIds = existingLinkedInstructions.map(
                          (instruction) => instruction.id
                      );

                      // Build query to exclude already-linked instructions at database level
                      const query =
                          existingLinkedInstructionIds.length > 0
                              ? {id: {$nin: existingLinkedInstructionIds}}
                              : {};

                      // Load only unlinked instructions from database
                      return this._OdmOsInstruction.loadOdmInstructions(
                          this._gameEditor._activeOrgId,
                          query
                      );
                  })
            : this._OdmOsInstruction.loadOdmInstructions(this._gameEditor._activeOrgId);

        return loadPromise.then((odmOsInstructions) => {
            this._logger.info(
                {
                    isEditMode: this._isEditMode,
                    isEditorMode: this._isEditorMode,
                    availableInstructionsCount: odmOsInstructions.length,
                },
                "AddInstructionsAction - Loaded instructions for dropdown"
            );
            const addInstructionsComponent = new AddInstructionsComponent(
                this._logger,
                this._selectedPlatformIds,
                odmOsInstructions,
                this._OdmOsInstruction,
                this._OdmOsPlatform,
                this._gameEditor._activeOrgId,
                this._userSession,
                this._isEditMode,
                this._instructionToEdit
            );

            let res; // Store result for use after veil closes

            // Display the Add Instructions veil (dontHide: true prevents auto-hide on resolve)
            return this._modalUserFeedback
                .displayCustom(addInstructionsComponent, {dontHide: true})
                .then((data) => {
                    // Store result and manually hide the veil
                    res = data;
                    return this._modalUserFeedback.hideDisplayed();
                })
                .then(() => {
                    // Handle result based on success/failure status
                    // Check for partial failures (some instructions linked, some failed/skipped)
                    if (res && !res.success && (res.failureCount > 0 || res.skippedCount > 0)) {
                        // Show summary veil with detailed error information
                        const summaryData = {
                            totalInstructions: res.totalInstructions,
                            totalPlatforms: res.totalPlatforms,
                            total: res.total,
                            successCount: res.successCount,
                            failureCount: res.failureCount,
                            skippedCount: res.skippedCount,
                            errors: res.errors,
                        };

                        const addInstructionsSummaryComponent = new AddInstructionsSummaryComponent(
                            summaryData,
                            this._logger
                        );

                        return this._modalUserFeedback
                            .displayCustom(addInstructionsSummaryComponent)
                            .then(() => {
                                // Refresh grid if any instructions were successfully linked
                                if (res.successCount > 0) {
                                    this._refreshGrid();
                                }
                            });
                    } else {
                        // Complete success - refresh grid to show updated instruction counts
                        this._refreshGrid();
                    }

                    return res;
                })
                .catch((err) => {
                    // Handle user cancellation or errors
                    this._modalUserFeedback.hideDisplayed();

                    // Only log errors that aren't user cancellations
                    if (!err.userRejected) {
                        this._logger.warn(
                            {error: err},
                            "AddInstructionsAction.run() - Error during dialog display"
                        );
                    }
                })
                .finally(() => {
                    // Clear the edit mode flag on completion
                    if (this._isEditMode) {
                        this._activeView.isEditingInstruction = false;
                        this._activeView._selectedInstructionForEdit = null;
                    }
                });
        });
    }

    /**
     * Refreshes the platform grid to update the Instructions Count column.
     * Called after successful instruction creation or linking.
     * @private
     */
    _refreshGrid() {
        if (!this._isEditMode) {
            // In bulk add mode, refresh the platform explorer grid
            if (
                this._activeView &&
                this._activeView.pagingFeed &&
                this._activeView.pagingFeed.feedUpdate
            ) {
                this._logger.info("AddInstructionsAction - Refreshing platform grid");
                this._activeView.pagingFeed.feedUpdate();
            }
        }
    }
}

module.exports = AddInstructionsAction;

/**
 * Dependency injection configuration for AngularJS.
 * Specifies the services and entities required by this action.
 */
AddInstructionsAction.$inject = [
    "$teve.logger", // Logger service
    "gameEditor.gameEditor", // Game editor instance
    "$teve.ui.userFeedback", // Modal user feedback service
    "$teve.entities.OdmInstructions", // OdmOsInstruction entity
    "$teve.entities.OdmOsPlatform", // OdmOsPlatform entity
    "$teve.userSession", // User session service
];

/**
 * Display configuration for the action in the UI.
 * Defines how the action appears in the platform explorer toolbar.
 */
AddInstructionsAction.$displayDetails = {
    type: "Action", // Action type
    name: "AddInstructionsAction", // Unique action name
    icon: "osmanager.add_instructions", // Icon identifier
    shown: true, // Visibility flag
    title: "Add Instructions", // Display title
    order: 3, // Display order in toolbar
};
